import { ProjectIssuanceConstants } from "../../../constants";
import { ProjectIssuanceSpecificProperty, ProjectIssuanceSpecificSchema } from "../../../models";

const BNG_SCHEMA_PROPERTY_ORDER = [
  "lpa",
  "nca",
  "lnrs",
  "habitatType",
  "habitat",
  "habitatCondition",
  "habitatDistinctiveness",
  "createdOrEnhanced",
  "unitHa",
  "unitKm",
  "quantityType",
];

const CARBON_SCHEMA_PROPERTY_ORDER = ["verificationStatus", "vintageStartDate", "vintageEndDate", "quantityType"];

/*
  Two columns, reading across: Nutrient / LPA, then designated site / LNRS, then eligibility / term.
  `quantityType` leads, which the panel's layout honours by placing that column at this key's index
  rather than appending it - the other two measurements list it last and are unaffected.
*/
const NN_SCHEMA_PROPERTY_ORDER = ["quantityType", "lpa", "designatedSite", "lnrs", "eligibility", "yearsEligible"];

/**
 * The schema's properties in the order the panel renders them, per measurement.
 *
 * Lives here rather than in the hook so it can be tested without loading the hook's service imports:
 * those reach the identity service, which is ESM-only and aborts a targeted `react-scripts test` run
 * that has not been given the package script's transform configuration.
 */
export const reorderIssuanceSchemaProperties = (
  parsedSchema: ProjectIssuanceSpecificSchema
): Record<string, ProjectIssuanceSpecificProperty> => {
  const schemaPropertyEntries = Object.entries(parsedSchema.properties ?? {}) as [
    string,
    ProjectIssuanceSpecificProperty,
  ][];
  const schemaPropertyMap = new Map(schemaPropertyEntries);
  const unitMeasurement = schemaPropertyMap.get("unitMeasurement")?.const;

  let preferredOrder: string[] = [];

  if (unitMeasurement === "Biodiversity") {
    preferredOrder = BNG_SCHEMA_PROPERTY_ORDER;
  } else if (unitMeasurement === "Carbon") {
    preferredOrder = CARBON_SCHEMA_PROPERTY_ORDER;
  } else if (unitMeasurement === ProjectIssuanceConstants.ISSUANCE_UNIT_MEASUREMENT_NUTRIENT_NEUTRALITY) {
    preferredOrder = NN_SCHEMA_PROPERTY_ORDER;
  }

  if (preferredOrder.length === 0) {
    return parsedSchema.properties;
  }

  const preferredOrderSet = new Set(preferredOrder);
  const reorderedEntries: [string, ProjectIssuanceSpecificProperty][] = [];

  preferredOrder.forEach((fieldKey) => {
    const fieldDefinition = schemaPropertyMap.get(fieldKey);
    if (fieldDefinition) {
      reorderedEntries.push([fieldKey, fieldDefinition]);
    }
  });

  schemaPropertyEntries.forEach(([fieldKey, fieldDefinition]) => {
    if (!preferredOrderSet.has(fieldKey)) {
      reorderedEntries.push([fieldKey, fieldDefinition]);
    }
  });

  return Object.fromEntries(reorderedEntries);
};
